// Minimal fetch wrapper. All requests go same-origin with the session cookie.
// A 401 response triggers a redirect to /login so stale UI can't linger.
//
// In the Teams iframe context cookies are often blocked (3rd-party), so we
// also send the session id as `Authorization: Bearer` if teamsContext stored
// one after SSO.

import { toastError } from "./toast";
import { getTeamsBearer, setTeamsBearer } from "./teamsContext";
import type {
  AccessOverview,
  Space,
  SpaceCreate,
  SpaceUpdate,
  SpaceMemberRole,
  Status,
  StatusCreate,
  StatusUpdate,
  ProjectTemplateCreate,
  ProjectTemplateItem,
  CustomFieldDef,
  CustomFieldDefCreate,
  CustomFieldDefUpdate,
  ApiCycle,
  ApiCycleSnapshot,
  ApiBurndown,
  CycleStatus,
  ApiFolder,
  ApiWorkItemType,
  ApiAutomationRule,
  ApiIntakeForm,
  ApiIntakeField,
  ApiGitConnection,
  ApiTaskGitLink,
  Task,
  TaskBatchUpdate,
  SuggestProjectStructureResult,
  ParseProjectPlanResult,
  TaskImportAnalyzeInput,
  TaskImportProposal,
  ProjectImportStructure,
  ProjectImportStructureResult,
  PersonalTasksBulk,
  PersonalTasksBulkResult,
  MyDayPlanResult,
  InfoFeedResponse,
  InfoEntry,
  InfoEntryCreate,
  InfoEntryUpdate,
  InfoGenerationState,
  AiFeaturesResponse,
  AiFeaturesUpdate,
  AiModelsResponse,
  AiProvider,
  AiProviderDto,
  AiProvidersResponse,
  AiProviderWrite,
  AiGlobalSettingsDto,
  AiSettingsUpdate,
  EmailSettingsDto,
  EmailSettingsUpdate,
  MailConnectionDto,
  FollowUpDto,
  FollowUpCreate,
  FollowUpPatch,
} from "@juuplan/shared";

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;
  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

interface ApiOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  /** If true, do NOT auto-redirect on 401 (used by useMe to decide UI). */
  silent401?: boolean;
  /** If true, do NOT auto-toast on mutation error. Caller surfaces it manually. */
  silentError?: boolean;
}

/**
 * Methoden, deren Fehler `api()` selbst als Toast meldet.
 *
 * BEWUSST NUR SCHREIBENDE METHODEN: ein fehlgeschlagener Hintergrund-GET
 * (Abfragen beim Mounten, Aktualisierung nach einem SSE-Ereignis, Zähler)
 * würde sonst Toasts am Fließband erzeugen.
 *
 * ABER — und das ist die Kehrseite, die lange niemand mitgelesen hat:
 * **ein fehlgeschlagener GET ist damit app-weit STUMM.** Wer hier ruft, ist
 * selbst dafür verantwortlich, den Fehler sichtbar zu machen. Und zwar nicht
 * nur als Meldung, sondern als ZUSTAND: der häufigste Fehler in dieser
 * Codebasis war `.catch(() => setRows([]))`. Das zeigt dem Nutzer eine leere
 * Liste, er liest „ich habe keine Nachfragen / keine Favoriten / keine
 * Treffer" — obwohl der Server nicht geantwortet hat. Ein solcher Fehler wird
 * NIE gemeldet, weil er plausibel aussieht; in `Favorites.tsx` stand er zwei
 * Monate (Audit 2026-09-04, dort auch in `FollowUps.tsx`, der Suchpalette und
 * fünf Admin-Listen gefunden).
 *
 * Regel für Aufrufstellen bei lesenden Abrufen:
 *  - „konnte nicht geladen werden" muss vom Leerzustand UNTERSCHEIDBAR sein
 *    (eigener Zustand, eigener Text) — ein Leerzustand bei Fehler ist eine
 *    Falschaussage.
 *  - Bei einem vom Nutzer ausgelösten Abruf zusätzlich `toastError`.
 *  - Bei einem absichtlich optionalen Abruf darf es still bleiben — dann mit
 *    einem Kommentar, WARUM.
 */
const MUTATION_METHODS = new Set(["POST", "PATCH", "PUT", "DELETE"]);

export async function api<T = unknown>(
  path: string,
  opts: ApiOptions = {}
): Promise<T> {
  const { body, headers, silent401, silentError, ...rest } = opts;
  const method = (rest.method ?? "GET").toUpperCase();
  const bearer = getTeamsBearer();
  const init: RequestInit = {
    credentials: "include",
    ...rest,
    headers: {
      Accept: "application/json",
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      ...(bearer ? { Authorization: `Bearer ${bearer}` } : {}),
      ...(headers as Record<string, string> | undefined),
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  };

  let res: Response;
  try {
    res = await fetch(path, init);
  } catch (err) {
    // Network failure (offline, CORS, DNS). Toast it for mutations so the user
    // notices their click had no effect.
    if (!silentError && MUTATION_METHODS.has(method)) {
      toastError("Netzwerkfehler — bitte erneut versuchen.");
    }
    throw err;
  }

  if (res.status === 401) {
    // Bearer turned out to be stale (e.g. expired). Drop it so the next page
    // load re-runs the Teams SSO exchange instead of looping on a dead token.
    if (bearer) setTeamsBearer(null);
    if (!silent401 && typeof window !== "undefined" && window.location.pathname !== "/login") {
      // Bring the user back to login (full reload to drop any in-memory state).
      // Guard: skip when already on /login to prevent the infinite-reload loop
      // that occurs when unauthenticated background calls fire on the login page.
      window.location.href = "/login";
    }
    throw new ApiError(401, "Unauthorized", null);
  }

  const text = await res.text();
  const parsed: unknown = text ? safeJson(text) : null;

  if (!res.ok) {
    const msg = extractErrorMessage(parsed) ?? res.statusText;
    if (!silentError && MUTATION_METHODS.has(method)) {
      toastError(msg || `Fehler ${res.status}`);
    }
    throw new ApiError(res.status, msg, parsed);
  }

  return parsed as T;
}

// ---------- spaces helpers ----------

export interface SpaceMemberItem {
  user_id: number;
  role: SpaceMemberRole;
  name: string;
  email: string;
  avatar_url: string | null;
}

export function listSpaces(): Promise<Space[]> {
  return api<Space[]>("/api/spaces");
}

export function createSpace(body: SpaceCreate): Promise<Space> {
  return api<Space>("/api/spaces", { method: "POST", body });
}

export function updateSpace(id: number, body: SpaceUpdate): Promise<Space> {
  return api<Space>(`/api/spaces/${id}`, { method: "PATCH", body });
}

export function deleteSpace(id: number): Promise<void> {
  return api<void>(`/api/spaces/${id}`, { method: "DELETE" });
}

export function listSpaceMembers(spaceId: number): Promise<SpaceMemberItem[]> {
  return api<SpaceMemberItem[]>(`/api/spaces/${spaceId}/members`);
}

export function addSpaceMember(
  spaceId: number,
  user_id: number,
  role: SpaceMemberRole
): Promise<{ space_id: number; user_id: number; role: SpaceMemberRole }> {
  return api(`/api/spaces/${spaceId}/members`, { method: "POST", body: { user_id, role } });
}

export function removeSpaceMember(
  spaceId: number,
  userId: number
): Promise<void> {
  return api<void>(`/api/spaces/${spaceId}/members/${userId}`, { method: "DELETE" });
}

export function moveProject(
  projectId: number,
  body: { space_id: number; folder_id?: number | null }
): Promise<{ id: number; space_id: number; folder_id: number | null }> {
  return api(`/api/projects/${projectId}/move`, { method: "PATCH", body });
}

// ---------- status CRUD helpers ----------

export function listStatuses(projectId: number): Promise<Status[]> {
  return api<Status[]>(`/api/projects/${projectId}/statuses`);
}

export function createStatus(
  projectId: number,
  body: StatusCreate
): Promise<Status> {
  return api<Status>(`/api/projects/${projectId}/statuses`, {
    method: "POST",
    body,
  });
}

export function updateStatus(id: number, body: StatusUpdate): Promise<Status> {
  return api<Status>(`/api/statuses/${id}`, { method: "PATCH", body });
}

export function reorderStatuses(
  projectId: number,
  orderedIds: number[]
): Promise<void> {
  return api<void>(`/api/projects/${projectId}/statuses/reorder`, {
    method: "POST",
    body: { orderedIds },
  });
}

export function deleteStatus(id: number): Promise<void> {
  return api<void>(`/api/statuses/${id}`, { method: "DELETE" });
}

// ---------- custom field definitions (D4) ----------

export function listCustomFields(projectId: number): Promise<CustomFieldDef[]> {
  return api<CustomFieldDef[]>(`/api/projects/${projectId}/custom-fields`);
}

export function createCustomField(
  projectId: number,
  body: CustomFieldDefCreate
): Promise<CustomFieldDef> {
  return api<CustomFieldDef>(`/api/projects/${projectId}/custom-fields`, {
    method: "POST",
    body,
  });
}

export function updateCustomField(
  id: number,
  body: CustomFieldDefUpdate
): Promise<CustomFieldDef> {
  return api<CustomFieldDef>(`/api/custom-fields/${id}`, {
    method: "PATCH",
    body,
  });
}

export function reorderCustomFields(
  projectId: number,
  orderedIds: number[]
): Promise<void> {
  return api<void>(`/api/projects/${projectId}/custom-fields/reorder`, {
    method: "POST",
    body: { orderedIds },
  });
}

export function deleteCustomField(id: number): Promise<void> {
  return api<void>(`/api/custom-fields/${id}`, { method: "DELETE" });
}

// ---------- template CRUD helpers ----------

export function createTemplate(body: ProjectTemplateCreate): Promise<ProjectTemplateItem> {
  return api<ProjectTemplateItem>("/api/templates", { method: "POST", body });
}

export function deleteTemplate(id: number): Promise<void> {
  return api<void>(`/api/templates/${id}`, { method: "DELETE" });
}

export function saveProjectAsTemplate(
  projectId: number,
  body: { name: string; description?: string | null }
): Promise<ProjectTemplateItem> {
  return api<ProjectTemplateItem>(`/api/projects/${projectId}/save-as-template`, {
    method: "POST",
    body,
  });
}

// ---------- views CRUD helpers ----------

export interface SavedView {
  id: number;
  project_id: number;
  owner_id: number;
  name: string;
  view_type: "table" | "gantt" | "kanban" | "calendar";
  visibility: "private" | "shared" | "project";
  spec: Record<string, unknown>;
}

export interface SavedViewCreate {
  name: string;
  view_type: SavedView["view_type"];
  visibility: SavedView["visibility"];
  spec: Record<string, unknown>;
}

export interface SavedViewUpdate {
  name?: string;
  visibility?: SavedView["visibility"];
  spec?: Record<string, unknown>;
}

export function listViews(projectId: number): Promise<SavedView[]> {
  return api<SavedView[]>(`/api/projects/${projectId}/views`);
}

export function createView(
  projectId: number,
  body: SavedViewCreate
): Promise<SavedView> {
  return api<SavedView>(`/api/projects/${projectId}/views`, {
    method: "POST",
    body,
  });
}

export function updateView(id: number, body: SavedViewUpdate): Promise<SavedView> {
  return api<SavedView>(`/api/views/${id}`, { method: "PATCH", body });
}

export function deleteView(id: number): Promise<void> {
  return api<void>(`/api/views/${id}`, { method: "DELETE" });
}

// ---------- cycle CRUD helpers (F3) ----------

export interface CycleCreate {
  name: string;
  start_date?: string | null;
  end_date?: string | null;
  status?: CycleStatus;
}

export interface CycleUpdate {
  name?: string;
  start_date?: string | null;
  end_date?: string | null;
  status?: CycleStatus;
}

export function listCycles(projectId: number): Promise<ApiCycle[]> {
  return api<ApiCycle[]>(`/api/projects/${projectId}/cycles`);
}

export function createCycle(
  projectId: number,
  body: CycleCreate
): Promise<ApiCycle> {
  return api<ApiCycle>(`/api/projects/${projectId}/cycles`, {
    method: "POST",
    body,
  });
}

export function updateCycle(
  cycleId: number,
  body: CycleUpdate
): Promise<ApiCycle> {
  return api<ApiCycle>(`/api/cycles/${cycleId}`, { method: "PATCH", body });
}

export function deleteCycle(cycleId: number): Promise<void> {
  return api<void>(`/api/cycles/${cycleId}`, { method: "DELETE" });
}

export function assignTaskToCycle(
  cycleId: number,
  taskId: number
): Promise<void> {
  return api<void>(`/api/cycles/${cycleId}/tasks/${taskId}`, {
    method: "PUT",
  });
}

export function unassignTaskFromCycle(
  cycleId: number,
  taskId: number
): Promise<void> {
  return api<void>(`/api/cycles/${cycleId}/tasks/${taskId}`, {
    method: "DELETE",
  });
}

export function listCycleSnapshots(
  cycleId: number
): Promise<ApiCycleSnapshot[]> {
  return api<ApiCycleSnapshot[]>(`/api/cycles/${cycleId}/snapshots`);
}

export function getCycleBurndown(cycleId: number): Promise<ApiBurndown> {
  return api<ApiBurndown>(`/api/cycles/${cycleId}/burndown`);
}

export function triggerCycleSnapshot(
  cycleId: number
): Promise<ApiCycleSnapshot> {
  return api<ApiCycleSnapshot>(`/api/cycles/${cycleId}/snapshot`, {
    method: "POST",
  });
}

export function getCycleVelocity(
  projectId: number,
  n?: number
): Promise<{ velocity: number | null }> {
  const query = n !== undefined ? `?n=${n}` : "";
  return api<{ velocity: number | null }>(
    `/api/projects/${projectId}/cycles/velocity${query}`
  );
}

// ---------- folder CRUD helpers (H1) ----------

export interface FolderCreate {
  name: string;
  sort_order?: number;
}

export interface FolderUpdate {
  name?: string;
  sort_order?: number;
}

export function listFolders(spaceId: number): Promise<ApiFolder[]> {
  return api<ApiFolder[]>(`/api/spaces/${spaceId}/folders`);
}

export function createFolder(
  spaceId: number,
  body: FolderCreate
): Promise<ApiFolder> {
  return api<ApiFolder>(`/api/spaces/${spaceId}/folders`, {
    method: "POST",
    body,
  });
}

export function updateFolder(
  folderId: number,
  body: FolderUpdate
): Promise<ApiFolder> {
  return api<ApiFolder>(`/api/folders/${folderId}`, { method: "PATCH", body });
}

export function deleteFolder(folderId: number): Promise<void> {
  return api<void>(`/api/folders/${folderId}`, { method: "DELETE" });
}

// ---------- work item type CRUD helpers (E6) ----------

export interface WorkItemTypeCreate {
  name: string;
  icon?: string | null;
  color?: string;
  is_default?: boolean;
  /** Ticket-number prefix (e.g. "BUG"); derived from the name when omitted. */
  prefix?: string | null;
}

export interface WorkItemTypeUpdate {
  name?: string;
  icon?: string | null;
  color?: string;
  is_default?: boolean;
  /** Ticket-number prefix; empty string clears it (type issues no numbers). */
  prefix?: string | null;
}

export function listWorkItemTypes(projectId: number): Promise<ApiWorkItemType[]> {
  return api<ApiWorkItemType[]>(`/api/projects/${projectId}/work-item-types`);
}

export function createWorkItemType(
  projectId: number,
  body: WorkItemTypeCreate
): Promise<ApiWorkItemType> {
  return api<ApiWorkItemType>(`/api/projects/${projectId}/work-item-types`, {
    method: "POST",
    body,
  });
}

export function updateWorkItemType(
  typeId: number,
  body: WorkItemTypeUpdate
): Promise<ApiWorkItemType> {
  return api<ApiWorkItemType>(`/api/work-item-types/${typeId}`, {
    method: "PATCH",
    body,
  });
}

export function deleteWorkItemType(typeId: number): Promise<void> {
  return api<void>(`/api/work-item-types/${typeId}`, { method: "DELETE" });
}

// ---------- automation rule CRUD helpers (I3) ----------

export interface AutomationRuleCreate {
  trigger_type?: string;
  from_status_id?: number | null;
  to_status_id?: number | null;
  action_type: string;
  action_params: Record<string, unknown>;
  condition?: Record<string, unknown>;
  enabled?: boolean;
}

export type AutomationRuleUpdate = Partial<AutomationRuleCreate>;

export function listAutomationRules(projectId: number): Promise<ApiAutomationRule[]> {
  return api<ApiAutomationRule[]>(`/api/projects/${projectId}/automation-rules`);
}

export function createAutomationRule(
  projectId: number,
  body: AutomationRuleCreate
): Promise<ApiAutomationRule> {
  return api<ApiAutomationRule>(`/api/projects/${projectId}/automation-rules`, {
    method: "POST",
    body,
  });
}

export function updateAutomationRule(
  ruleId: number,
  body: AutomationRuleUpdate
): Promise<ApiAutomationRule> {
  return api<ApiAutomationRule>(`/api/automation-rules/${ruleId}`, {
    method: "PATCH",
    body,
  });
}

export function deleteAutomationRule(ruleId: number): Promise<void> {
  return api<void>(`/api/automation-rules/${ruleId}`, { method: "DELETE" });
}

// ---------- intake form CRUD helpers (G3) ----------

export interface IntakeFormCreate {
  name: string;
  field_config: ApiIntakeField[];
  target_phase_id?: number | null;
  enabled?: boolean;
}

export type IntakeFormUpdate = Partial<IntakeFormCreate>;

/** Response shape for create — includes one-time token fields. */
export interface IntakeFormCreateResponse extends ApiIntakeForm {
  public_token: string;
  share_path: string;
}

/** Response shape for rotate-token — one-time token fields only. */
export interface IntakeFormRotateResponse {
  public_token: string;
  share_path: string;
}

export function listIntakeForms(projectId: number): Promise<ApiIntakeForm[]> {
  return api<ApiIntakeForm[]>(`/api/projects/${projectId}/intake-forms`);
}

export function createIntakeForm(
  projectId: number,
  body: IntakeFormCreate
): Promise<IntakeFormCreateResponse> {
  return api<IntakeFormCreateResponse>(`/api/projects/${projectId}/intake-forms`, {
    method: "POST",
    body,
  });
}

export function updateIntakeForm(
  formId: number,
  body: IntakeFormUpdate
): Promise<ApiIntakeForm> {
  return api<ApiIntakeForm>(`/api/intake-forms/${formId}`, {
    method: "PATCH",
    body,
  });
}

export function rotateIntakeToken(
  formId: number
): Promise<IntakeFormRotateResponse> {
  return api<IntakeFormRotateResponse>(`/api/intake-forms/${formId}/rotate-token`, {
    method: "POST",
  });
}

export function deleteIntakeForm(formId: number): Promise<void> {
  return api<void>(`/api/intake-forms/${formId}`, { method: "DELETE" });
}

// ---------- triage helpers (G3c) ----------

/**
 * List tasks pending triage review for a project.
 * Returns tasks with triageState === 'pending' (excluded from normal task lists).
 */
export function listTriage(projectId: number): Promise<Task[]> {
  return api<Task[]>(`/api/projects/${projectId}/triage`);
}

/**
 * Accept or reject a triage task.
 * Accept: makes the task a normal task (triageState → null).
 * Reject: archives the task (triageState → 'rejected').
 */
export function decideTriage(
  taskId: number,
  decision: "accept" | "reject"
): Promise<void> {
  return api<void>(`/api/tasks/${taskId}/triage`, {
    method: "POST",
    body: { decision },
  });
}

// ---------- git connection CRUD helpers (J3) ----------

export interface GitConnectionCreate {
  provider: "github" | "github_ees" | "gitlab" | "gitlab_selfhosted";
  /** Only 'pat' is accepted in v1. */
  auth_type: "pat";
  /** Required for github_ees and gitlab_selfhosted providers. */
  base_url?: string;
  repo_identifier?: string;
  default_branch?: string;
  /** The PAT credential value. */
  credential: string;
}

/** Partial update — auth_type is intentionally excluded (immutable post-create). */
export type GitConnectionUpdate = Partial<
  Pick<GitConnectionCreate, "base_url" | "repo_identifier" | "default_branch" | "credential">
>;

/** Response shape for create — includes one-time webhook_secret + webhook_url. */
export interface GitConnectionCreateResponse extends ApiGitConnection {
  webhook_secret: string;
  webhook_url: string;
}

/** Response shape for rotate-webhook-secret — one-time fields only. */
export interface GitConnectionRotateResponse {
  webhook_secret: string;
  webhook_url: string;
}

export function listGitConnections(projectId: number): Promise<ApiGitConnection[]> {
  return api<ApiGitConnection[]>(`/api/projects/${projectId}/git-connections`);
}

export function createGitConnection(
  projectId: number,
  body: GitConnectionCreate
): Promise<GitConnectionCreateResponse> {
  return api<GitConnectionCreateResponse>(`/api/projects/${projectId}/git-connections`, {
    method: "POST",
    body,
  });
}

export function updateGitConnection(
  id: number,
  body: GitConnectionUpdate
): Promise<ApiGitConnection> {
  return api<ApiGitConnection>(`/api/git-connections/${id}`, {
    method: "PATCH",
    body,
  });
}

export function rotateGitWebhookSecret(id: number): Promise<GitConnectionRotateResponse> {
  return api<GitConnectionRotateResponse>(`/api/git-connections/${id}/rotate-webhook-secret`, {
    method: "POST",
  });
}

export function deleteGitConnection(id: number): Promise<void> {
  return api<void>(`/api/git-connections/${id}`, { method: "DELETE" });
}

// ---------- repo links (P3b) ----------

/** A repository available to be linked (from enabled git servers). */
export interface AvailableRepo {
  id: number;
  server_id: number;
  full_name: string;
  default_branch: string | null;
  web_url: string | null;
  provider: string;
}

/** A project↔repo link, enriched with repo metadata. */
export interface ApiRepoLink {
  id: number;
  repository_id: number;
  full_name: string;
  provider: string;
  ai_automation_enabled: boolean;
  create_from_git_enabled: boolean;
  last_reconciled_at: string | null;
}

/**
 * List repositories the caller could link to this project.
 * Only repos from enabled git servers are returned.
 */
export function getAvailableRepos(projectId: number): Promise<AvailableRepo[]> {
  return api<AvailableRepo[]>(`/api/projects/${projectId}/available-repos`);
}

/** List all repo links for this project. */
export function getRepoLinks(projectId: number): Promise<ApiRepoLink[]> {
  return api<ApiRepoLink[]>(`/api/projects/${projectId}/repo-links`);
}

/**
 * Create a repo link. Uses silentError so the caller can surface the 409
 * duplicate toast inline with a friendlier message.
 */
export function createRepoLink(
  projectId: number,
  repositoryId: number
): Promise<ApiRepoLink> {
  return api<ApiRepoLink>(`/api/projects/${projectId}/repo-links`, {
    method: "POST",
    body: { repository_id: repositoryId },
    silentError: true,
  });
}

/** Delete a repo link by linkId, scoped to projectId. */
export function deleteRepoLink(projectId: number, linkId: number): Promise<void> {
  return api<void>(`/api/projects/${projectId}/repo-links/${linkId}`, {
    method: "DELETE",
  });
}

/**
 * Toggle ai_automation_enabled on a repo link.
 * Uses silentError so the component can surface failures inline.
 */
export function patchRepoLink(
  projectId: number,
  linkId: number,
  patch: { ai_automation_enabled?: boolean; create_from_git_enabled?: boolean }
): Promise<ApiRepoLink> {
  return api<ApiRepoLink>(`/api/projects/${projectId}/repo-links/${linkId}`, {
    method: "PATCH",
    body: patch,
    silentError: true,
  });
}

// ---------- task git-links (J3b) ----------

export function listTaskGitLinks(taskId: number): Promise<ApiTaskGitLink[]> {
  return api<ApiTaskGitLink[]>(`/api/tasks/${taskId}/git-links`);
}

// ---------- batch task edit (K5b) ----------

/**
 * Bulk-update multiple tasks in a single request.
 *
 * The server applies the patch atomically — any unauthorized or non-existent
 * id causes the entire request to fail with a 4xx and zero writes.
 *
 * @param body `{ ids, patch }` — patch may contain `status_id` or `priority`
 *             (the only fields exposed in the bulk UI per design).
 * @returns `{ updated: number[] }` — ids that were actually written.
 */
export function batchUpdateTasks(
  body: TaskBatchUpdate
): Promise<{ updated: number[] }> {
  return api<{ updated: number[] }>("/api/tasks/batch", {
    method: "PATCH",
    body,
  });
}

/**
 * Bulk-move tasks into another phase.
 *
 * A task's phase is NOT a `TaskUpdate` field — cross-phase moves go through the
 * dedicated `/reorder` endpoint (which also keeps sort_order and sub-tasks
 * consistent, see server lib/taskReorder). There is no batch variant, so we
 * issue one reorder per id *sequentially* (sort_order 0 → prepend); running
 * them in parallel would race the sort_order shifts inside the target phase.
 *
 * Best-effort per task: a rejected move (e.g. a sub-task, which must stay in
 * its parent's phase) is collected rather than aborting the whole run. Returns
 * the ids that moved successfully.
 */
export async function moveTasksToPhase(
  ids: number[],
  phaseId: number
): Promise<{ moved: number[]; failed: number[] }> {
  const moved: number[] = [];
  const failed: number[] = [];
  for (const id of ids) {
    try {
      await api(`/api/tasks/${id}/reorder`, {
        method: "PATCH",
        body: { phase_id: phaseId, sort_order: 0 },
        silentError: true,
      });
      moved.push(id);
    } catch {
      failed.push(id);
    }
  }
  return { moved, failed };
}

// ---------- SSE stream ticket (L5) ----------

/**
 * Mint a single-use stream ticket for EventSource authentication.
 * The ticket is valid for <=60 s and must be used immediately on connect.
 *
 * @returns The ticket string to append as ?ticket=<value> to the stream URL.
 */
export function mintStreamTicket(): Promise<string> {
  // silent401: background SSE-setup call; a 401 here means the user is not
  // authenticated — do not hard-redirect, the caller's error path handles it.
  // silentError: as a POST this would otherwise auto-toast on any non-401
  // failure (e.g. 403 for external guests); the SSE layer handles failures by
  // backing off and reconnecting, so a background ticket-mint must stay silent.
  return api<{ ticket: string }>("/api/stream/ticket", {
    method: "POST",
    silent401: true,
    silentError: true,
  }).then((res) => res.ticket);
}

// ---------- analytics helpers (N2/N3) ----------

/** One interval bucket returned by the throughput endpoint. */
export interface ThroughputBucket {
  label: string;
  count: number;
}

export interface ThroughputResponse {
  buckets: ThroughputBucket[];
  total: number;
  from: string;
  to: string;
  interval: "week" | "day";
}

export interface CycleTimeSummary {
  average_days: number | null;
  median_days: number | null;
  sample_count: number;
}

export interface CycleTimeBucket {
  bucket_days: number;
  count: number;
}

export interface CycleTimeResponse {
  summary: CycleTimeSummary;
  distribution: CycleTimeBucket[];
}

export interface StatusDistributionItem {
  status_id: number;
  status_name: string;
  category: string;
  sort_order: number;
  count: number;
}

export interface TimeInStatusItem {
  status_id: number;
  status_name: string;
  category: string;
  average_hours: number;
  sample_count: number;
}

export interface VelocityTrendItem {
  cycle_id: number;
  name: string;
  end_date: string | null;
  completed: number;
}

export interface VelocityResponse {
  velocity: number | null;
  trend: VelocityTrendItem[];
}

export interface WorkloadItem {
  user_id: number;
  user_name: string;
  open_task_count: number;
  estimated_hours: number;
  actual_hours: number;
  overdue_count: number;
}

export function getAnalyticsThroughput(
  projectId: number,
  opts?: { from?: string; to?: string; interval?: "week" | "day" }
): Promise<ThroughputResponse> {
  const params = new URLSearchParams();
  if (opts?.from) params.set("from", opts.from);
  if (opts?.to) params.set("to", opts.to);
  if (opts?.interval) params.set("interval", opts.interval);
  const qs = params.toString();
  return api<ThroughputResponse>(
    `/api/projects/${projectId}/analytics/throughput${qs ? `?${qs}` : ""}`
  );
}

export function getAnalyticsCycleTime(projectId: number): Promise<CycleTimeResponse> {
  return api<CycleTimeResponse>(`/api/projects/${projectId}/analytics/cycle-time`);
}

export function getAnalyticsStatusDistribution(
  projectId: number
): Promise<StatusDistributionItem[]> {
  return api<StatusDistributionItem[]>(
    `/api/projects/${projectId}/analytics/status-distribution`
  );
}

export function getAnalyticsTimeInStatus(
  projectId: number
): Promise<TimeInStatusItem[]> {
  return api<TimeInStatusItem[]>(
    `/api/projects/${projectId}/analytics/time-in-status`
  );
}

export function getAnalyticsVelocity(
  projectId: number,
  n?: number
): Promise<VelocityResponse> {
  const qs = n !== undefined ? `?n=${n}` : "";
  return api<VelocityResponse>(`/api/projects/${projectId}/analytics/velocity${qs}`);
}

export function getWorkload(projectId: number): Promise<WorkloadItem[]> {
  return api<WorkloadItem[]>(`/api/projects/${projectId}/workload`);
}

// ---------- workload heatmap (#64, User×Week) ----------

export interface WorkloadWeekCell {
  count: number;
  estimated_hours: number;
}

export interface WeekColumn {
  key: string;
  label: string;
  start_date: string;
}

export interface UserWeeklyWorkload {
  user_id: number;
  user_name: string;
  /** Same length + order as WorkloadHeatmapResponse.week_columns. */
  weeks: WorkloadWeekCell[];
  /** Tasks with no start/end/deadline at all — never guessed onto "this week". */
  no_date: WorkloadWeekCell;
}

export interface WorkloadHeatmapResponse {
  week_columns: WeekColumn[];
  users: UserWeeklyWorkload[];
}

export function getWorkloadWeekly(
  projectId: number,
  weeks?: number
): Promise<WorkloadHeatmapResponse> {
  const qs = weeks !== undefined ? `?weeks=${weeks}` : "";
  return api<WorkloadHeatmapResponse>(`/api/projects/${projectId}/workload/weekly${qs}`);
}

// ---------- goals / OKRs helpers (O3) ----------

export type GoalStatus = "on_track" | "at_risk" | "off_track" | "done";

export interface ApiGoal {
  id: number;
  title: string;
  description: string | null;
  owner_id: number;
  status: GoalStatus;
  target_date: string | null;
  parent_goal_id: number | null;
  created_at: string;
  updated_at: string;
}

/** Shape returned by GET /api/goals/:id — includes rollup progress (0-100) and links. */
export interface ApiGoalDetail extends ApiGoal {
  progress: number;
  links: ApiGoalLink[];
}

export interface ApiGoalLink {
  id: number;
  goal_id: number;
  project_id: number | null;
  task_id: number | null;
  created_at: string;
}

export interface GoalCreate {
  title: string;
  description?: string | null;
  status?: GoalStatus;
  target_date?: string | null;
  parent_goal_id?: number | null;
}

export type GoalUpdate = Partial<GoalCreate>;

export interface GoalLinkCreate {
  project_id?: number;
  task_id?: number;
}

export function listGoals(): Promise<ApiGoal[]> {
  return api<ApiGoal[]>("/api/goals");
}

export function getGoal(id: number): Promise<ApiGoalDetail> {
  return api<ApiGoalDetail>(`/api/goals/${id}`);
}

export function createGoal(body: GoalCreate): Promise<ApiGoal> {
  return api<ApiGoal>("/api/goals", { method: "POST", body });
}

export function updateGoal(id: number, body: GoalUpdate): Promise<ApiGoal> {
  return api<ApiGoal>(`/api/goals/${id}`, { method: "PATCH", body });
}

export function deleteGoal(id: number): Promise<void> {
  return api<void>(`/api/goals/${id}`, { method: "DELETE" });
}

export function addGoalLink(id: number, body: GoalLinkCreate): Promise<ApiGoalLink> {
  return api<ApiGoalLink>(`/api/goals/${id}/links`, { method: "POST", body });
}

export function removeGoalLink(goalId: number, linkId: number): Promise<void> {
  return api<void>(`/api/goals/${goalId}/links/${linkId}`, { method: "DELETE" });
}

// ---------- inbox helpers (O7) ----------

/** Triage state values mirroring INBOX_STATES on the server. */
export type InboxItemState = "unread" | "snoozed" | "done";

/** Normalized inbox item returned by GET /api/inbox. */
export interface InboxItem {
  source_type: string;
  source_id: number;
  title: string;
  link: string | null;
  task_id: number | null;
  project_id: number | null;
  state: InboxItemState;
  snooze_until: string | null;
  created_at: string;
  /**
   * Der Eintrag steht auf „erledigt", WEIL seine Aufgabe erledigt ist (nicht,
   * weil ihn jemand abgehakt hat). Serverseitig beim Lesen gebildet — ein
   * „wieder aufnehmen" wuerde daran nichts aendern, deshalb bietet die Zeile
   * es dann nicht an. Aeltere Server liefern das Feld nicht, darum optional.
   */
  task_done?: boolean;
}

export interface InboxTriage {
  state: InboxItemState;
  snooze_until?: string | null;
}

/**
 * Fetch inbox items, optionally filtered by state.
 * @param state Filter by triage state. Omit to fetch all (unread + snoozed + done).
 */
export function listInbox(state?: InboxItemState): Promise<{ items: InboxItem[] }> {
  const qs = state ? `?state=${encodeURIComponent(state)}` : "";
  return api<{ items: InboxItem[] }>(`/api/inbox${qs}`);
}

/**
 * Triage (mark done / snooze / mark unread) a single inbox item.
 * Returns 204 — resolves void.
 */
export function patchInbox(
  sourceType: string,
  sourceId: number,
  body: InboxTriage
): Promise<void> {
  return api<void>(`/api/inbox/${encodeURIComponent(sourceType)}/${sourceId}`, {
    method: "PATCH",
    body,
  });
}

/**
 * Fetch the current unread inbox count for the badge.
 */
export function inboxCount(): Promise<{ unread: number }> {
  // silent401: background badge call; a 401 must not hard-redirect.
  return api<{ unread: number }>("/api/inbox/count", { silent401: true });
}

// ---------- admin user management helpers (admin-only) ----------

/** Public DTO returned by GET /api/admin/users — never includes secrets. */
export interface AdminUser {
  id: number;
  email: string;
  name: string;
  given_name: string | null;
  family_name: string | null;
  is_admin: boolean;
  is_active: boolean;
  is_external: boolean;
  last_login_at: string | null;
  /**
   * Bot-Account (Migration 0085) samt Owner, in dessen Auftrag er handelt.
   * Optional getippt, damit die Admin-Tabelle auch gegen eine Server-Version
   * ohne diese Felder funktioniert — dann fehlt lediglich das Bot-Badge.
   */
  is_bot?: boolean;
  bot_owner_id?: number | null;
}

export interface AdminUserPatch {
  is_admin?: boolean;
  is_active?: boolean;
  name?: string;
}

/** Ein Space-Zugriff des Users — direkt oder über Abteilung(en) geerbt. */
export interface AdminUserAccessSpace {
  id: number;
  name: string;
  role: "viewer" | "editor";
  via: "space" | "department";
  departments: string[];
}

/** Eine Gast-Einladung (projekt- oder aufgaben-skopiert) inkl. Status. */
export interface AdminUserAccessInvite {
  id: number;
  project_id: number;
  project_name: string | null;
  task_id: number | null;
  task_title: string | null;
  permission: "read" | "edit";
  status: "active" | "expired" | "revoked";
  expires_at: string;
  accepted_at: string | null;
  revoked_at: string | null;
  created_at: string;
  note: string | null;
}

export interface AdminUserAccess {
  departments: { id: number; name: string }[];
  spaces: AdminUserAccessSpace[];
  invites: AdminUserAccessInvite[];
}

export interface AdminInviteResendResult {
  id: number;
  project_id: number;
  task_id: number | null;
  email: string;
  permission: string;
  expires_at: string;
  one_time_token: string;
  invite_url: string;
}

/**
 * List all users for admin management.
 * Returns a secret-safe projection — no passwords, no tokens.
 */
export function listAdminUsers(): Promise<AdminUser[]> {
  return api<AdminUser[]>("/api/admin/users");
}

/**
 * Toggle is_admin or is_active for a user.
 * Uses silentError so callers can surface the 400 guard messages inline.
 */
export function updateAdminUser(
  id: number,
  patch: AdminUserPatch
): Promise<AdminUser> {
  return api<AdminUser>(`/api/admin/users/${id}`, {
    method: "PATCH",
    body: patch,
    silentError: true,
  });
}

/** Zugriffs-Detail: Abteilungen, Spaces und Gast-Einladungen eines Users. */
export function getAdminUserAccess(id: number): Promise<AdminUserAccess> {
  return api<AdminUserAccess>(`/api/admin/users/${id}/access`);
}

/** Eine offene Aufgabe des Gastes, die vor dem Löschen übertragen werden muss. */
export interface AdminGuestOpenTask {
  id: number;
  title: string;
  project_id: number;
  project_name: string | null;
}

/** 409-Body von DELETE /api/admin/users/:id, wenn der Gast offene Aufgaben hat. */
export interface AdminGuestDeleteBlocked {
  error: string;
  open_tasks: AdminGuestOpenTask[];
}

/**
 * Externen Gast löschen: widerruft alle Einladungen, beendet Sessions und
 * entfernt den Account (Fallback: Deaktivierung, wenn noch ein unbekannter FK
 * auf den Gast verweist). Interne Benutzer lehnt der Server mit 400 ab.
 * Hat der Gast offene Aufgaben, antwortet der Server mit 409 + `open_tasks`
 * (siehe `AdminGuestDeleteBlocked`) — dann `reassignTo` mitgeben und erneut
 * aufrufen. Erledigte Aufgaben werden automatisch (ohne reassignTo) an ihren
 * jeweiligen Ersteller übertragen.
 */
export function deleteAdminUser(
  id: number,
  reassignTo?: number
): Promise<{ deleted: boolean; deactivated: boolean }> {
  const qs = reassignTo ? `?reassign_to=${reassignTo}` : "";
  return api<{ deleted: boolean; deactivated: boolean }>(
    `/api/admin/users/${id}${qs}`,
    { method: "DELETE", silentError: true }
  );
}

/** Gast-Zugang zurücksetzen: alter Link wird widerrufen, neuer gemailt. */
export function resendAdminInvite(
  inviteId: number
): Promise<AdminInviteResendResult> {
  return api<AdminInviteResendResult>(
    `/api/admin/invites/${inviteId}/resend`,
    { method: "POST", silentError: true }
  );
}

/** Einzelne Gast-Einladung widerrufen (Admin nutzt die Owner-Route mit). */
export function revokeAdminInvite(inviteId: number): Promise<void> {
  return api<void>(`/api/invites/${inviteId}`, {
    method: "DELETE",
    silentError: true,
  });
}

// ---------- AI settings helpers (admin-only) ----------

/** Re-export the shared provider DTO for component convenience. */
export type { AiProviderDto, AiProviderWrite, AiGlobalSettingsDto } from "@juuplan/shared";

export interface AiTestResult {
  ok: boolean;
  status?: number;
}

/** List all configured providers + the global switch/default. */
export function getAiProviders(): Promise<AiProvidersResponse> {
  return api<AiProvidersResponse>("/api/admin/ai-providers");
}

export function createAiProvider(body: AiProviderWrite): Promise<AiProviderDto> {
  return api<AiProviderDto>("/api/admin/ai-providers", { method: "POST", body, silentError: true });
}

export function updateAiProvider(id: number, body: AiProviderWrite): Promise<AiProviderDto> {
  return api<AiProviderDto>(`/api/admin/ai-providers/${id}`, {
    method: "PUT",
    body,
    silentError: true,
  });
}

export function deleteAiProvider(id: number): Promise<void> {
  return api<void>(`/api/admin/ai-providers/${id}`, { method: "DELETE", silentError: true });
}

/** Probe ONE provider (works even before it is enabled). */
export function testAiProvider(id: number): Promise<AiTestResult> {
  return api<AiTestResult>(`/api/admin/ai-providers/${id}/test`, {
    method: "POST",
    silentError: true,
  });
}

/** Discover the models ONE provider offers (server-side, keyed). */
export function getAiProviderModels(id: number): Promise<AiModelsResponse> {
  return api<AiModelsResponse>(`/api/admin/ai-providers/${id}/models`, { silentError: true });
}

/** The global master switch + default provider. */
export function getAiSettings(): Promise<AiGlobalSettingsDto> {
  return api<AiGlobalSettingsDto>("/api/admin/ai-settings");
}

export function updateAiSettings(body: AiSettingsUpdate): Promise<AiGlobalSettingsDto> {
  return api<AiGlobalSettingsDto>("/api/admin/ai-settings", {
    method: "PUT",
    body,
    silentError: true,
  });
}

/** Per-feature model + thinking overrides (+ global defaults). */
export function getAiFeatures(): Promise<AiFeaturesResponse> {
  return api<AiFeaturesResponse>("/api/admin/ai-settings/features");
}

export function updateAiFeatures(body: AiFeaturesUpdate): Promise<AiFeaturesResponse> {
  return api<AiFeaturesResponse>("/api/admin/ai-settings/features", {
    method: "PUT",
    body,
    silentError: true,
  });
}

// ---------- Email integration (admin) ----------

export function getEmailSettings(): Promise<EmailSettingsDto> {
  return api<EmailSettingsDto>("/api/admin/email-settings");
}

export function updateEmailSettings(body: EmailSettingsUpdate): Promise<EmailSettingsDto> {
  return api<EmailSettingsDto>("/api/admin/email-settings", {
    method: "PUT",
    body,
    silentError: true,
  });
}

export function testEmailSettings(): Promise<{ ok: boolean; reason?: string }> {
  return api<{ ok: boolean; reason?: string }>("/api/admin/email-settings/test", {
    method: "POST",
    silentError: true,
  });
}

/** The caller's delegated-mailbox connection status. */
export function getMailConnection(): Promise<MailConnectionDto> {
  return api<MailConnectionDto>("/api/me/mail-connection", { silentError: true });
}

// ---------- Follow-ups (auto "Nachfrage an Kollegen") ----------

export function getFollowUps(): Promise<FollowUpDto[]> {
  return api<FollowUpDto[]>("/api/follow-ups");
}

export function createFollowUp(body: FollowUpCreate): Promise<FollowUpDto> {
  return api<FollowUpDto>("/api/follow-ups", { method: "POST", body, silentError: true });
}

export function updateFollowUp(id: number, body: FollowUpPatch): Promise<FollowUpDto> {
  return api<FollowUpDto>(`/api/follow-ups/${id}`, { method: "PATCH", body, silentError: true });
}

export function deleteFollowUp(id: number): Promise<void> {
  return api<void>(`/api/follow-ups/${id}`, { method: "DELETE", silentError: true });
}

export type { FollowUpDto, FollowUpCreate, FollowUpPatch } from "@juuplan/shared";

/** AI-draft a follow-up email subject + message from a task/list context. */
export function aiDraftFollowUp(ctx: {
  personal_task_id?: number;
  task_id?: number;
}): Promise<{ subject: string; message: string }> {
  return api<{ subject: string; message: string }>("/api/ai/draft-follow-up", {
    method: "POST",
    body: ctx,
    silentError: true,
  });
}

export function aiSummarizeTask(taskId: number): Promise<{ summary: string }> {
  return api<{ summary: string }>("/api/ai/summarize-task", { method: "POST", body: { task_id: taskId } });
}

export function aiSummarizeProject(projectId: number): Promise<{ summary: string }> {
  // silentError: 503 (AI disabled) is handled inline by the caller; other
  // errors are swallowed at the project view (no recovery action to offer).
  return api<{ summary: string }>("/api/ai/summarize-project", {
    method: "POST",
    body: { project_id: projectId },
    silentError: true,
  });
}

export function aiSuggestSubtasks(taskId: number): Promise<{ suggestions: string[] }> {
  return api<{ suggestions: string[] }>("/api/ai/suggest-subtasks", { method: "POST", body: { task_id: taskId } });
}

/**
 * Summarise the open tasks in a personal-task list.
 * 503 (AI disabled) is handled inline by the caller; other errors are swallowed
 * (no recovery action to offer).
 */
export function aiSummarizeList(listId: number): Promise<{ summary: string }> {
  return api<{ summary: string }>("/api/ai/summarize-list", {
    method: "POST",
    body: { list_id: listId },
    silentError: true,
  });
}

/**
 * Suggest 3-7 concrete next-action steps for a personal task.
 * Non-503 errors are auto-toasted by api() (no silentError flag).
 */
export function aiSuggestSteps(personalTaskId: number): Promise<{ suggestions: string[] }> {
  return api<{ suggestions: string[] }>("/api/ai/suggest-steps", {
    method: "POST",
    body: { personal_task_id: personalTaskId },
  });
}

/**
 * Summarise a single personal task (notes, steps, comments).
 * 503 (AI disabled) is handled inline by the caller; other errors are swallowed.
 */
export function aiSummarizePersonalTask(
  personalTaskId: number,
): Promise<{ summary: string }> {
  return api<{ summary: string }>("/api/ai/summarize-personal-task", {
    method: "POST",
    body: { personal_task_id: personalTaskId },
    silentError: true,
  });
}

export function aiParseTask(
  text: string,
  projectId: number
): Promise<{ title: string; priority?: string; due_date?: string }> {
  return api<{ title: string; priority?: string; due_date?: string }>("/api/ai/parse-task", {
    method: "POST",
    body: { text, project_id: projectId },
  });
}

export function aiSuggestProjectStructure(
  name: string,
  description?: string | null
): Promise<SuggestProjectStructureResult> {
  return api<SuggestProjectStructureResult>("/api/ai/suggest-project-structure", {
    method: "POST",
    body: { name, description: description ?? undefined },
    silentError: true,
  });
}

/**
 * Parse an uploaded Markdown project plan into a full project structure
 * (name + description + phases → tasks → subtasks). Propose-only: the caller
 * then creates the project via createProjectFromStructure.
 */
export function aiParseProjectPlan(plan: string): Promise<ParseProjectPlanResult> {
  return api<ParseProjectPlanResult>("/api/ai/parse-project-plan", {
    method: "POST",
    body: { plan },
    silentError: true,
  });
}

/**
 * KI „Aufgabenliste importieren" (#1297): Vorschlag + Rückfrage. Propose-only —
 * angelegt wird erst nach Bestätigung über importStructureIntoProject /
 * createPersonalTasksBulk / createProjectFromStructure. silentError: der Dialog
 * zeigt 503/502 selbst an.
 */
export function aiImportTaskList(input: TaskImportAnalyzeInput): Promise<TaskImportProposal> {
  return api<TaskImportProposal>("/api/ai/import-task-list", {
    method: "POST",
    body: input,
    silentError: true,
  });
}

export function importStructureIntoProject(
  projectId: number,
  body: ProjectImportStructure
): Promise<ProjectImportStructureResult> {
  return api<ProjectImportStructureResult>(`/api/projects/${projectId}/import-structure`, {
    method: "POST",
    body,
  });
}

export function createPersonalTasksBulk(body: PersonalTasksBulk): Promise<PersonalTasksBulkResult> {
  return api<PersonalTasksBulkResult>("/api/personal-tasks/bulk", { method: "POST", body });
}

/**
 * Ask the AI to rank the caller's own + assigned open tasks into a focused day
 * plan. Propose-only: the result is rendered inline, nothing is persisted.
 * `day` defaults to today (server-side) when omitted. silentError: the caller
 * handles 503 (AI disabled) inline.
 */
export function aiPlanMyDay(day?: string): Promise<MyDayPlanResult> {
  return api<MyDayPlanResult>("/api/ai/plan-my-day", {
    method: "POST",
    body: day ? { day } : {},
    silentError: true,
  });
}

/**
 * Builds the create/update payload for one provider, omitting api_key when the
 * user left the field blank (keeps the existing key on the server). Shape depends
 * on the provider: 'openai_compatible' sends base_url (no region), 'bedrock' sends
 * region (no base_url — the endpoint is derived server-side).
 */
export function buildAiProviderPayload(
  provider: AiProvider,
  name: string,
  base_url: string,
  region: string,
  model: string,
  allowed_models: string[],
  api_key: string,
  enabled: boolean
): AiProviderWrite {
  const trimmedKey = api_key.trim();
  const payload: AiProviderWrite =
    provider === "bedrock"
      ? { provider: "bedrock", name, region, model, allowed_models, enabled }
      : { provider: "openai_compatible", name, base_url, model, allowed_models, enabled };
  if (trimmedKey.length > 0) {
    payload.api_key = trimmedKey;
  }
  return payload;
}

// ---------- admin git server helpers (P6b) ----------

/**
 * Public DTO for a Git server — access_token is NEVER included.
 * Mirrors GitServerDto from apps/server/src/routes/admin/gitServers.ts.
 */
export interface AdminGitServer {
  id: number;
  name: string;
  provider: string;
  base_url: string;
  enabled: boolean;
  /** true when an access_token ciphertext exists on the server — the token itself is never returned. */
  has_token: boolean;
  created_at: string;
  updated_at: string;
}

/** Public DTO for a discovered repository. */
export interface AdminGitRepository {
  id: number;
  server_id: number;
  external_id: string;
  full_name: string;
  default_branch: string | null;
  web_url: string | null;
  last_synced_at: string | null;
}

export interface AdminGitServerCreate {
  provider: "gitlab_selfhosted" | "github";
  name: string;
  /** Required for gitlab_selfhosted; optional/defaulted for github. */
  base_url?: string;
  /** Write-only: stored encrypted. Never returned. */
  access_token?: string;
  enabled?: boolean;
}

export interface AdminGitServerUpdate {
  name?: string;
  base_url?: string;
  /** Provide to rotate; omit to keep existing. */
  access_token?: string;
  enabled?: boolean;
}

/** List all registered Git servers. Token is NEVER included in the response. */
export function listGitServers(): Promise<AdminGitServer[]> {
  return api<AdminGitServer[]>("/api/admin/git-servers");
}

/** Register a new Git server. */
export function createGitServer(body: AdminGitServerCreate): Promise<AdminGitServer> {
  return api<AdminGitServer>("/api/admin/git-servers", { method: "POST", body, silentError: true });
}

/** Partially update a Git server. Omitting access_token keeps the existing ciphertext. */
export function updateGitServer(id: number, body: AdminGitServerUpdate): Promise<AdminGitServer> {
  return api<AdminGitServer>(`/api/admin/git-servers/${id}`, { method: "PATCH", body, silentError: true });
}

/** Delete a Git server by id. */
export function deleteGitServer(id: number): Promise<void> {
  return api<void>(`/api/admin/git-servers/${id}`, { method: "DELETE" });
}

/** Test the stored token against the upstream provider. Returns { ok, repo_count? }. */
export function testGitServer(id: number): Promise<{ ok: boolean; repo_count?: number }> {
  return api<{ ok: boolean; repo_count?: number }>(`/api/admin/git-servers/${id}/test`, {
    method: "POST",
    silentError: true,
  });
}

/** Trigger a repository discovery run. Returns { discovered } — the count upserted. */
export function discoverGitServerRepos(id: number): Promise<{ discovered: number }> {
  return api<{ discovered: number }>(`/api/admin/git-servers/${id}/discover`, {
    method: "POST",
    silentError: true,
  });
}

/** List discovered repositories, optionally filtered by server. */
export function listGitRepositories(serverId?: number): Promise<AdminGitRepository[]> {
  const qs = serverId !== undefined ? `?server_id=${serverId}` : "";
  return api<AdminGitRepository[]>(`/api/admin/git-repositories${qs}`);
}

// ---------- admin git user map helpers ----------
// Maps a provider identity (email and/or username, per git server) to a juuplan
// user, so the reconcile CREATE tier can assign an auto-created task to the
// person who did the work in Git. Mirrors the DTO in
// apps/server/src/routes/admin/gitUserMap.ts.

/** Public DTO for one git-identity → user mapping (joins the target user). */
export interface AdminGitUserMap {
  id: number;
  server_id: number;
  git_email: string | null;
  git_username: string | null;
  user_id: number;
  user_name: string | null;
  user_email: string | null;
  created_at: string;
  updated_at: string;
}

export interface AdminGitUserMapCreate {
  server_id: number;
  user_id: number;
  git_email?: string;
  git_username?: string;
}

export interface AdminGitUserMapUpdate {
  user_id?: number;
  git_email?: string | null;
  git_username?: string | null;
}

/** List identity mappings, optionally filtered by git server. */
export function listGitUserMaps(serverId?: number): Promise<AdminGitUserMap[]> {
  const qs = serverId !== undefined ? `?server_id=${serverId}` : "";
  return api<AdminGitUserMap[]>(`/api/admin/git-user-map${qs}`);
}

/** Create a git-identity → user mapping. */
export function createGitUserMap(body: AdminGitUserMapCreate): Promise<AdminGitUserMap> {
  return api<AdminGitUserMap>("/api/admin/git-user-map", { method: "POST", body, silentError: true });
}

/** Partially update a mapping. */
export function updateGitUserMap(id: number, body: AdminGitUserMapUpdate): Promise<AdminGitUserMap> {
  return api<AdminGitUserMap>(`/api/admin/git-user-map/${id}`, { method: "PATCH", body, silentError: true });
}

/** Delete a mapping by id. */
export function deleteGitUserMap(id: number): Promise<void> {
  return api<void>(`/api/admin/git-user-map/${id}`, { method: "DELETE" });
}

// ---------- admin reconcile overview helpers (P6b-2) ----------

/** Compact counts object returned inside run summaries. */
export interface ReconcileRunCounts {
  proposed_create?: number;
  proposed_close?: number;
  applied?: number;
  [key: string]: number | undefined;
}

/** Latest-run summary embedded in a ReconcileOverviewRow. */
export interface ReconcileLatestRun {
  id: number;
  status: string;
  trigger: string;
  finished_at: string | null;
  counts: ReconcileRunCounts;
}

/**
 * One row from GET /api/admin/reconcile-overview.
 * One entry per project_repo_link across ALL projects.
 */
export interface ReconcileOverviewRow {
  link_id: number;
  project_id: number;
  project_name: string;
  repository_full_name: string;
  server_name: string;
  provider: string;
  ai_automation_enabled: boolean;
  last_reconciled_at: string | null;
  latest_run: ReconcileLatestRun | null;
  pending_proposal_count: number;
}

/** Full run-history entry from GET /api/admin/reconcile-overview/:linkId/runs. */
export interface AdminReconcileRun {
  id: number;
  status: string;
  trigger: string;
  started_at: string;
  finished_at: string | null;
  counts: ReconcileRunCounts;
  summary: string | null;
  triggered_by: number | null;
}

/** Response from POST /api/admin/reconcile-overview/:linkId/trigger. */
export interface AdminTriggerResult {
  run_id: number;
  status: string;
  counts: ReconcileRunCounts;
}

/**
 * Fetch the cross-project reconcile overview.
 * Returns one row per project_repo_link across all projects.
 */
export function getReconcileOverview(): Promise<ReconcileOverviewRow[]> {
  return api<ReconcileOverviewRow[]>("/api/admin/reconcile-overview");
}

/**
 * Fetch the full run history for a link, newest-first.
 * Returns an empty array when the link has no runs.
 */
export function getAdminLinkRuns(linkId: number): Promise<AdminReconcileRun[]> {
  return api<AdminReconcileRun[]>(`/api/admin/reconcile-overview/${linkId}/runs`);
}

/**
 * Admin-level manual trigger for a link.
 * Calls runReconcile with trigger='manual' and the requesting admin's id.
 * Uses silentError so the component can surface failures inline.
 */
export function adminTriggerReconcile(linkId: number): Promise<AdminTriggerResult> {
  return api<AdminTriggerResult>(`/api/admin/reconcile-overview/${linkId}/trigger`, {
    method: "POST",
    silentError: true,
  });
}

// ---------- "Was ist neu" info feed helpers ----------

/** Admin-facing entry shape — includes soft-delete state absent from the user-facing InfoEntry. */
export interface InfoEntryAdmin extends InfoEntry {
  deleted_at: string | null;
}

/** Fetch the info feed for the current user, newest-first. */
export function fetchInfoEntries(limit?: number): Promise<InfoFeedResponse> {
  return api<InfoFeedResponse>(`/api/info/entries${limit ? `?limit=${limit}` : ""}`);
}

/** Mark the info feed as seen, resetting the unread badge count. */
export function postInfoSeen(): Promise<{ unread_count: number }> {
  return api<{ unread_count: number }>("/api/info/seen", { method: "POST" });
}

/** List all info entries for admin management, optionally including soft-deleted ones. */
export function adminListInfoEntries(includeDeleted = false): Promise<{ entries: InfoEntryAdmin[] }> {
  return api<{ entries: InfoEntryAdmin[] }>(
    `/api/admin/info/entries${includeDeleted ? "?include_deleted=true" : ""}`
  );
}

/** Create a new info entry. */
export function adminCreateInfoEntry(body: InfoEntryCreate): Promise<InfoEntryAdmin> {
  return api<InfoEntryAdmin>("/api/admin/info/entries", { method: "POST", body });
}

/** Update an existing info entry. */
export function adminUpdateInfoEntry(id: number, body: InfoEntryUpdate): Promise<InfoEntryAdmin> {
  return api<InfoEntryAdmin>(`/api/admin/info/entries/${id}`, { method: "PATCH", body });
}

/** Soft-delete an info entry. */
export function adminDeleteInfoEntry(id: number): Promise<void> {
  return api<void>(`/api/admin/info/entries/${id}`, { method: "DELETE" });
}

/**
 * Trigger AI generation of a new info entry.
 * Uses silentError so the caller can surface 503 (AI disabled) / 409 (already
 * running) inline with a friendlier message.
 */
export function adminGenerateInfo(): Promise<{ status: string }> {
  return api<{ status: string }>("/api/admin/info/generate", { method: "POST", silentError: true });
}

/** Poll the current AI generation state (idle / running / error). */
export function adminGetInfoStatus(): Promise<InfoGenerationState> {
  return api<InfoGenerationState>("/api/admin/info/status");
}

/** Partial "Was ist neu" generator config; only provided fields are written. */
export interface InfoConfigInput {
  /** SOURCE git repository (null clears it). Without one, generation errors. */
  git_repository_id?: number | null;
  /** Toggle the daily auto-generation schedule. */
  auto_schedule_enabled?: boolean;
  /** Hour of day (0..23, Europe/Berlin) the daily auto-generation runs at. */
  auto_schedule_hour?: number;
}

export interface InfoConfigResult {
  git_repository_id: number | null;
  auto_schedule_enabled: boolean;
  auto_schedule_hour: number;
}

/**
 * Update the "Was ist neu" generator config (source repository and/or the daily
 * auto-generation schedule). Partial: only the fields present in `config` are
 * written server-side, so toggling the schedule never clears the source repo.
 */
export function adminSetInfoConfig(
  config: InfoConfigInput
): Promise<InfoConfigResult> {
  return api<InfoConfigResult>("/api/admin/info/config", {
    method: "PUT",
    body: config,
    silentError: true,
  });
}

/**
 * Rebuild prep: soft-delete all AI-generated entries and rewind the commit
 * cursor so the next generation reprocesses the full commit window. Returns the
 * number of AI entries removed. The caller then triggers generation.
 */
export function adminResetInfo(): Promise<{ removed: number }> {
  return api<{ removed: number }>("/api/admin/info/reset", { method: "POST", silentError: true });
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function extractErrorMessage(body: unknown): string | null {
  if (body && typeof body === "object" && "error" in body) {
    const err = (body as { error: unknown }).error;
    if (typeof err === "string" && err.length > 0) return err;
  }
  return null;
}

// -------- Zugriffs-Uebersicht ("wer kann das hier sehen?") --------
//
// Sichtbar fuer jeden mit Zugriff auf das jeweilige Objekt (auch fuer
// Betrachter). Externe Gaeste erhalten 403 — die Transparenz gilt im Haus.

/** Wer kann auf dieses Projekt zugreifen, und woher kommt das Recht? */
export function getProjectAccess(projectId: number): Promise<AccessOverview> {
  return api<AccessOverview>(`/api/projects/${projectId}/access`);
}

/** Wer kann auf diese Aufgabenliste zugreifen? */
export function getListAccess(listId: number): Promise<AccessOverview> {
  return api<AccessOverview>(`/api/lists/${listId}/access`);
}

/** Wer kann auf diesen Space zugreifen — und was wird damit erreichbar? */
export function getSpaceAccess(spaceId: number): Promise<AccessOverview> {
  return api<AccessOverview>(`/api/spaces/${spaceId}/access`);
}
